/*
 * Copyright © 2025 Gavin Sawyer. All rights reserved.
 */

import { ChangeDetectionStrategy, Component, inject, input, type InputSignal, signal, type WritableSignal }                                                                                               from "@angular/core";
import { UserCredential }                                                                                                                                                                                 from "@angular/fire/auth";
import { addDoc, collection, type CollectionReference, deleteDoc, type DocumentReference, type DocumentSnapshot, Firestore, getDocs, query, type QuerySnapshot, runTransaction, type Transaction, where } from "@angular/fire/firestore";
import { FormControl, FormGroup, ReactiveFormsModule, Validators }                                                                                                                                        from "@angular/forms";
import { type BagDocument }                                                                                                                                                                               from "@bowstring/interfaces";
import { AuthenticationService, BagService, EllipsesService }                                                                                                                                             from "@bowstring/services";
import { CaptionComponent, LabelComponent, SymbolComponent }                                                                                                                                              from "../content";
import { DividerComponent, FlexboxContainerComponent, SectionComponent }                                                                                                                                  from "../layout and organization";
import { ButtonComponent }                                                                                                                                                                                from "../menus and actions";
import { type SheetComponent }                                                                                                                                                                            from "../presentation";
import { FormComponent, TextFieldInputComponent }                                                                                                                                                         from "../selection and input";


@Component(
  {
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports:         [
      ButtonComponent,
      CaptionComponent,
      DividerComponent,
      FlexboxContainerComponent,
      FormComponent,
      LabelComponent,
      ReactiveFormsModule,
      SectionComponent,
      SymbolComponent,
      TextFieldInputComponent,
    ],
    selector:        "bowstring--signin",
    styleUrl:        "SigninComponent.sass",
    templateUrl:     "SigninComponent.html",

    standalone: true,
  },
)
export class SigninComponent {

  private readonly bagService: BagService = inject<BagService>(BagService);
  private readonly firestore: Firestore   = inject<Firestore>(Firestore);

  protected readonly authenticationService: AuthenticationService                                                              = inject<AuthenticationService>(AuthenticationService);
  protected readonly ellipsesService: EllipsesService                                                                          = inject<EllipsesService>(EllipsesService);
  protected readonly signinWithPasskeyFormWorking$: WritableSignal<boolean>                                                    = signal<boolean>(false);
  protected readonly signinWithPasswordFormGroup: FormGroup<{ "email": FormControl<string>, "password": FormControl<string> }> = new FormGroup<{ "email": FormControl<string>, "password": FormControl<string> }>(
    {
      email:    new FormControl<string>(
        "",
        {
          nonNullable: true,
          validators:  [
            Validators.email,
            Validators.required,
          ],
        },
      ),
      password: new FormControl<string>(
        "",
        {
          nonNullable: true,
          validators:  [ Validators.required ],
        },
      ),
    },
  );
  protected readonly signinWithPasswordFormWorking$: WritableSignal<boolean>                                                   = signal<boolean>(false);

  public readonly sheetComponentInput$: InputSignal<SheetComponent | undefined> = input<SheetComponent | undefined>(
    undefined,
    { alias: "sheetComponent" },
  );

  protected signinWithPasskeyFormSubmit(openModel$?: SheetComponent["openModel$"]): void {
    const bagContents: BagDocument["contents"] | undefined                              = this.bagService.bagDocument$()?.contents;
    const bagDocumentReference: DocumentReference<BagDocument, BagDocument> | undefined = this.bagService.bagDocumentReference$();
    const bagsCollectionReference                                                       = collection(
      this.firestore,
      "bags",
    ) as CollectionReference<BagDocument, BagDocument>;

    this.signinWithPasskeyFormWorking$.set(true);

    ((callback: (bagContents?: BagDocument["contents"]) => Promise<void>): Promise<void> => {
      if (bagDocumentReference)
        return deleteDoc<BagDocument, BagDocument>(bagDocumentReference).then<void, never>(
          (): Promise<void> => callback(bagContents),
          (error: Error): never => {
            console.error("Something went wrong.");

            throw error;
          },
        );

      return callback();
    })(
      (bagContents?: BagDocument["contents"]): Promise<void> => this.authenticationService.signInWithPasskey().then<void, void>(
        ({ user: { uid: userId } }: UserCredential): void => {
          if (bagContents)
            getDocs<BagDocument, BagDocument>(
              query<BagDocument, BagDocument>(
                bagsCollectionReference,
                where(
                  "userId",
                  "==",
                  userId,
                ),
              ),
            ).then<void, never>(
              ({ docs: bagDocumentSnapshots }: QuerySnapshot<BagDocument, BagDocument>): Promise<void> => ((callback: () => Promise<void>): Promise<void> => {
                const bagDocumentReference: DocumentReference<BagDocument, BagDocument> | undefined = bagDocumentSnapshots[0]?.ref;

                if (bagDocumentReference)
                  return runTransaction<void>(
                    this.firestore,
                    (transaction: Transaction): Promise<void> => transaction.get<BagDocument, BagDocument>(bagDocumentReference).then<void, never>(
                      async (bagDocumentSnapshot: DocumentSnapshot<BagDocument, BagDocument>): Promise<void> => {
                        const bagContentsAccount: BagDocument["contents"] | undefined = bagDocumentSnapshot.data()?.contents;

                        if (bagContentsAccount)
                          return transaction.update(
                            bagDocumentReference,
                            {
                              contents: [
                                ...bagContentsAccount.map<BagDocument["contents"][number]>(
                                  (bagContentAccount: BagDocument["contents"][number]): BagDocument["contents"][number] => ({
                                    ...bagContentAccount,
                                    quantity: bagContentAccount.quantity + (bagContents.find(
                                      ({ stripeProductDocumentId: bagContentStripeProductDocumentId }: BagDocument["contents"][number]): boolean => bagContentStripeProductDocumentId === bagContentAccount.stripeProductDocumentId,
                                    )?.quantity || 0),
                                  }),
                                ),
                                ...bagContents.filter(
                                  ({ stripeProductDocumentId: bagContentStripeProductDocumentId }: BagDocument["contents"][number]): boolean => bagContentsAccount.every(
                                    ({ stripeProductDocumentId: bagContentAccountStripeProductDocumentId }: BagDocument["contents"][number]): boolean => bagContentAccountStripeProductDocumentId !== bagContentStripeProductDocumentId,
                                  ),
                                ),
                              ],
                            },
                          ) && void (0);

                        return callback();
                      },
                      (error: Error): never => {
                        console.error("Something went wrong.");

                        throw error;
                      },
                    ),
                  );

                return callback();
              })(
                (): Promise<void> => addDoc<BagDocument, BagDocument>(
                  bagsCollectionReference,
                  {
                    contents: bagContents,
                    userId,
                  },
                ).then<void, never>(
                  (): void => void (0),
                  (error: Error): never => {
                    console.error("Something went wrong.");

                    throw error;
                  },
                ),
              ),
              (error: Error): never => {
                console.error("Something went wrong.");

                throw error;
              },
            );
        },
        async (error: unknown): Promise<void> => {
          const userId: string | undefined = this.authenticationService.user$()?.uid;

          if (bagContents && userId)
            return addDoc<BagDocument, BagDocument>(
              bagsCollectionReference,
              {
                contents: bagContents,
                userId,
              },
            ).then<void, never>(
              (): void => void (0),
              (error: Error): never => {
                console.error("Something went wrong.");

                throw error;
              },
            );

          throw error;
        },
      ),
    ).finally(
      (): void => ((callback: () => void): void => {
        if (openModel$) {
          openModel$.set(false);

          setTimeout(
            callback,
            180,
          );
        } else
          callback();
      })(
        (): void => this.signinWithPasskeyFormWorking$.set(false),
      ),
    );
  }
  protected signinWithPasswordFormSubmit(openModel$?: SheetComponent["openModel$"]): void {
    const email: string | undefined    = this.signinWithPasswordFormGroup.value.email;
    const password: string | undefined = this.signinWithPasswordFormGroup.value.password;

    if (email && password) {
      const bagContents: BagDocument["contents"] | undefined                              = this.bagService.bagDocument$()?.contents;
      const bagDocumentReference: DocumentReference<BagDocument, BagDocument> | undefined = this.bagService.bagDocumentReference$();
      const bagsCollectionReference                                                       = collection(
        this.firestore,
        "bags",
      ) as CollectionReference<BagDocument, BagDocument>;

      this.signinWithPasswordFormWorking$.set(true);

      ((callback: () => Promise<void>): Promise<void> => {
        if (bagDocumentReference)
          return deleteDoc<BagDocument, BagDocument>(bagDocumentReference).then<void, never>(
            (): Promise<void> => callback(),
            (error: Error): never => {
              console.error("Something went wrong.");

              throw error;
            },
          );

        return callback();
      })(
        (): Promise<void> => this.authenticationService.signInWithEmailAndPassword(
          email,
          password,
        ).then<void, void>(
          ({ user: { uid: userId } }: UserCredential): void => {
            if (bagContents)
              getDocs<BagDocument, BagDocument>(
                query<BagDocument, BagDocument>(
                  bagsCollectionReference,
                  where(
                    "userId",
                    "==",
                    userId,
                  ),
                ),
              ).then<void, never>(
                ({ docs: bagDocumentSnapshots }: QuerySnapshot<BagDocument, BagDocument>): Promise<void> => ((callback: () => Promise<void>): Promise<void> => {
                  const bagDocumentReference: DocumentReference<BagDocument, BagDocument> | undefined = bagDocumentSnapshots[0]?.ref;

                  if (bagDocumentReference)
                    return runTransaction<void>(
                      this.firestore,
                      (transaction: Transaction): Promise<void> => transaction.get<BagDocument, BagDocument>(bagDocumentReference).then<void, never>(
                        async (bagDocumentSnapshot: DocumentSnapshot<BagDocument, BagDocument>): Promise<void> => {
                          const bagContentsAccount: BagDocument["contents"] | undefined = bagDocumentSnapshot.data()?.contents;

                          if (bagContentsAccount)
                            return transaction.update(
                              bagDocumentReference,
                              {
                                contents: [
                                  ...bagContentsAccount.map<BagDocument["contents"][number]>(
                                    (bagContentAccount: BagDocument["contents"][number]): BagDocument["contents"][number] => ({
                                      ...bagContentAccount,
                                      quantity: bagContentAccount.quantity + (bagContents.find(
                                        ({ stripeProductDocumentId: bagContentStripeProductDocumentId }: BagDocument["contents"][number]): boolean => bagContentStripeProductDocumentId === bagContentAccount.stripeProductDocumentId,
                                      )?.quantity || 0),
                                    }),
                                  ),
                                  ...bagContents.filter(
                                    ({ stripeProductDocumentId: bagContentStripeProductDocumentId }: BagDocument["contents"][number]): boolean => bagContentsAccount.every(
                                      ({ stripeProductDocumentId: bagContentAccountStripeProductDocumentId }: BagDocument["contents"][number]): boolean => bagContentAccountStripeProductDocumentId !== bagContentStripeProductDocumentId,
                                    ),
                                  ),
                                ],
                              },
                            ) && void (0);

                          return callback();
                        },
                        (error: Error): never => {
                          console.error("Something went wrong.");

                          throw error;
                        },
                      ),
                    );

                  return callback();
                })(
                  (): Promise<void> => addDoc<BagDocument, BagDocument>(
                    bagsCollectionReference,
                    {
                      contents: bagContents,
                      userId,
                    },
                  ).then<void, never>(
                    (): void => void (0),
                    (error: Error): never => {
                      console.error("Something went wrong.");

                      throw error;
                    },
                  ),
                ),
                (error: Error): never => {
                  console.error("Something went wrong.");

                  throw error;
                },
              );
          },
          async (error: unknown): Promise<void> => {
            const userId: string | undefined = this.authenticationService.user$()?.uid;

            if (bagContents && userId)
              return addDoc<BagDocument, BagDocument>(
                bagsCollectionReference,
                {
                  contents: bagContents,
                  userId,
                },
              ).then<void, never>(
                (): void => void (0),
                (error: Error): never => {
                  console.error("Something went wrong.");

                  throw error;
                },
              );

            throw error;
          },
        ),
      ).finally(
        (): void => ((callback: () => void) => {
          if (openModel$) {
            openModel$.set(false);

            setTimeout(
              callback,
              180,
            );
          } else
            callback();
        })(
          (): void => {
            this.signinWithPasswordFormGroup.reset();
            this.signinWithPasswordFormWorking$.set(false);
          },
        ),
      );
    }
  }

}
